// Fetch wrapper for the api: JSON in/out, cookie auth, ApiError with code and fieldErrors.
import type { ApiErrorBody, ErrorCode } from "@zeiterfassung/shared";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly fieldErrors: Record<string, string>;

  constructor(status: number, body: ApiErrorBody["error"]) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.fieldErrors = body.fieldErrors ?? {};
  }
}

// The api could not be reached (offline, DNS, server down).
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("network");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export async function api<T>(
  method: Method,
  path: string,
  options: { body?: unknown; formData?: FormData; signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  let body: BodyInit | undefined;
  if (options.formData) body = options.formData;
  else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body,
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!response.ok) {
    const error = (json as ApiErrorBody | null)?.error ?? {
      code: "internal" as const,
      message: response.statusText,
    };
    throw new ApiError(response.status, error);
  }
  return json as T;
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>("GET", path, { signal });
export const post = <T>(path: string, body?: unknown) => api<T>("POST", path, { body });
export const put = <T>(path: string, body?: unknown) => api<T>("PUT", path, { body });
export const patch = <T>(path: string, body?: unknown) => api<T>("PATCH", path, { body });
export const del = <T>(path: string) => api<T>("DELETE", path);

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}
